import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { PoweredBy } from '@/components/print/PoweredBy';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate } from '@/lib/utils';
import { paymentMethodLabel } from '@/lib/payment-labels';
import api from '@/lib/api';

/** Payload returned by `POST /customers/:id/ledger/payment` (T-482). */
export interface KhataPaymentReceipt {
  entryId: string;
  /** POS-I133: the number to quote later (the posted journal entry's number); null for a cheque still to clear. */
  receiptNumber?: string | null;
  cashierName?: string | null;
  customerName: string;
  amount: number;
  paymentMethod: string;
  reference: string | null;
  bankAccountName: string | null;
  chequeNo: string | null;
  notes: string | null;
  balanceBefore: number;
  balanceAfter: number;
  createdAt: string;
}

interface Props {
  receipt: KhataPaymentReceipt | null;
  onClose: () => void;
}

/**
 * Payment-receipt variant of the sale receipt: same Dialog chrome, `#receipt` print target and
 * tenant header as `ReceiptView`, so the existing print stylesheet applies (no new template system).
 */
export function PaymentReceiptView({ receipt, onClose }: Props) {
  const { t } = useTranslation('receipt');
  const { data: tenantSettings } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 60_000,
    enabled: !!receipt,
  });
  const receiptFooter: string = tenantSettings?.settings?.receiptFooter ?? '';

  const footer = (
    <div className="flex gap-2 print:hidden">
      <Button variant="outline" onClick={onClose} className="flex-1">
        {t('close', { ns: 'common' })}
      </Button>
      <Button onClick={() => window.print()} className="flex-1">
        <Printer className="me-1 h-4 w-4" />
        {t('print', { ns: 'common' })}
      </Button>
    </div>
  );

  return (
    <Dialog
      visible={!!receipt}
      onHide={onClose}
      header={t('paymentReceiptTitle')}
      footer={footer}
      style={{ width: '24rem' }}
      draggable={false}
      resizable={false}
      contentClassName="p-0"
    >
      {receipt && (
        <div className="p-4 text-foreground">
          <div id="receipt" className="space-y-3">
            <div className="border-b pb-3 text-center">
              <h2 className="text-lg font-bold">{tenantSettings?.name}</h2>
              <p className="text-xs text-muted-foreground">{t('paymentReceiptTitle')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{formatDate(receipt.createdAt)}</p>
              <p className="mt-1 text-xs font-mono">
                {t('receiptNo')}: {receipt.receiptNumber ?? receipt.entryId.slice(0, 8).toUpperCase()}
              </p>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span>{t('receivedFrom')}</span>
                <span className="font-medium">{receipt.customerName}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('paymentMethod')}</span>
                <span>{paymentMethodLabel(receipt.paymentMethod)}</span>
              </div>
              {receipt.bankAccountName && (
                <div className="flex justify-between">
                  <span>{t('bankAccount')}</span>
                  <span>{receipt.bankAccountName}</span>
                </div>
              )}
              {receipt.cashierName && (
                <div className="flex justify-between">
                  <span>{t('cashier')}</span>
                  <span>{receipt.cashierName}</span>
                </div>
              )}
              {receipt.reference && (
                <div className="flex justify-between">
                  <span>{receipt.chequeNo ? t('chequeNo') : t('reference')}</span>
                  <span className="font-mono">{receipt.reference}</span>
                </div>
              )}
            </div>
            <div className="space-y-1 border-t pt-2 text-sm">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('previousBalance')}</span>
                <span>{formatCurrency(receipt.balanceBefore)}</span>
              </div>
              <div className="flex justify-between text-base font-bold">
                <span>{t('amountReceived')}</span>
                <span>{formatCurrency(receipt.amount)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span>{t('remainingBalance')}</span>
                <span className="font-semibold">{formatCurrency(receipt.balanceAfter)}</span>
              </div>
            </div>
            {receipt.notes && (
              <p className="border-t pt-2 text-xs text-muted-foreground">{t('notes')}: {receipt.notes}</p>
            )}
            <p className="border-t pt-3 text-center text-xs text-muted-foreground">
              {receiptFooter || t('thankYou')}
            </p>
            {/* POS-I240: product identity; no tenant setting can remove it. */}
            <PoweredBy />
          </div>
        </div>
      )}
    </Dialog>
  );
}
